import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Collapse,
  Skeleton,
  Alert,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RTooltip,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { Trade, TradeType, Client, Employee } from '../types';
import { dailyBrokerageContribution } from '../utils/brokerage';
import { istDay } from '../utils/formatters';
import { tradeService } from '../services/trade.service';
import { clientService } from '../services/client.service';
import { salaryService } from '../services/salary.service';
import PageHeader from '../components/common/PageHeader';

// ============================================================================
// PALETTE — matches Analytics / ClientDetail premium style
// ============================================================================

const C = {
  primary: '#0A0A0A',
  primaryDark: '#262626',
  positive: '#059669',
  negative: '#DC2626',
  warning: '#D97706',
  blue: '#2563EB',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  textMuted: '#94A3B8',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  pageBg: '#F8FAFC',
  surface: '#FFFFFF',
};

const CHART_PALETTE = [
  '#4F46E5', // indigo
  '#059669', // green
  '#D97706', // amber
  '#DC2626', // red
  '#2563EB', // blue
  '#DB2777', // pink
  '#7C3AED', // violet
  '#0891B2', // cyan
  '#65A30D', // lime
  '#EA580C', // orange
  '#0284C7', // sky
  '#9333EA', // purple
];

const TYPE_COLOR: Record<string, string> = {
  [TradeType.EQUITY]: '#2563EB',
  [TradeType.STOCK]: '#0891B2',
  [TradeType.INTRA_STOCK]: '#DB2777',
  [TradeType.FUTURE]: '#4F46E5',
  [TradeType.OPTION]: '#D97706',
};

// ============================================================================
// FORMATTERS
// ============================================================================

const inr = (val: number): string =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);

const inrPrecise = (val: number): string =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);

const inrCompact = (val: number): string => {
  const abs = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (abs >= 1_00_00_000) return `${sign}₹${(abs / 1_00_00_000).toFixed(2)}Cr`;
  if (abs >= 1_00_000) return `${sign}₹${(abs / 1_00_000).toFixed(2)}L`;
  if (abs >= 1000) return `${sign}₹${(abs / 1000).toFixed(1)}K`;
  return `${sign}₹${abs.toFixed(0)}`;
};

// ============================================================================
// DATE HELPERS
// ============================================================================

type DateMode = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'all';

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function isToday(d: Date): boolean {
  return isSameDay(d, new Date());
}
function weekStart(d: Date): Date {
  const dow = d.getDay();
  return addDays(startOfDay(d), -dow);
}
function weekEnd(d: Date): Date {
  return addDays(weekStart(d), 6);
}
function monthStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function monthEnd(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function quarterStart(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3, 1);
}
function quarterEnd(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3 + 3, 0);
}
function yearStart(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}
function yearEnd(d: Date): Date {
  return new Date(d.getFullYear(), 11, 31);
}
function getDateRange(mode: DateMode, sel: Date): { start: Date; end: Date } | null {
  if (mode === 'all') return null;
  if (mode === 'day') {
    const s = startOfDay(sel);
    return { start: s, end: new Date(s.getTime() + 86400000 - 1) };
  }
  if (mode === 'week') return { start: weekStart(sel), end: weekEnd(sel) };
  if (mode === 'month') return { start: monthStart(sel), end: monthEnd(sel) };
  if (mode === 'quarter') return { start: quarterStart(sel), end: quarterEnd(sel) };
  return { start: yearStart(sel), end: yearEnd(sel) };
}
function dateRangeLabel(mode: DateMode, sel: Date): string {
  if (mode === 'all') return 'All Time';
  if (mode === 'day')
    return sel.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const fmt = (d: Date) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  if (mode === 'week') return `${fmt(weekStart(sel))} – ${fmt(weekEnd(sel))}`;
  if (mode === 'month')
    return sel.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  if (mode === 'quarter') {
    const q = Math.floor(sel.getMonth() / 3) + 1;
    return `Q${q} ${sel.getFullYear()}`;
  }
  return `${sel.getFullYear()}`;
}
function bumpDate(mode: DateMode, sel: Date, dir: -1 | 1): Date {
  if (mode === 'day') return addDays(sel, dir);
  if (mode === 'week') return addDays(sel, 7 * dir);
  if (mode === 'month') {
    return new Date(sel.getFullYear(), sel.getMonth() + dir, 1);
  }
  if (mode === 'quarter') {
    return new Date(sel.getFullYear(), sel.getMonth() + 3 * dir, 1);
  }
  if (mode === 'year') {
    return new Date(sel.getFullYear() + dir, sel.getMonth(), 1);
  }
  return sel;
}
function isNextDisabled(mode: DateMode, sel: Date): boolean {
  if (mode === 'all') return true;
  const rng = getDateRange(mode, sel);
  if (!rng) return true;
  return rng.end >= new Date();
}

// ============================================================================
// TYPES
// ============================================================================

interface ClientBreakdown {
  clientId: string;
  clientName: string;
  revenue: number;
  tradeCount: number;
  turnover: number;
  openTrades: number;
  closedTrades: number;
  tradeTypes: Record<string, number>;
}

interface DealerStats {
  dealer: string;
  clients: ClientBreakdown[];
  totalRevenue: number;
  totalTurnover: number;
  totalTrades: number;
  totalOpen: number;
  totalClosed: number;
  clientCount: number;
  avgRevenuePerTrade: number;
  avgRevenuePerClient: number;
  tradeTypes: Record<string, number>;
  revenueByType: Record<string, number>;
}

// ============================================================================
// REVENUE CALCULATION (identical to CommissionCalculator / DealerRevenuePopup)
// ============================================================================

// Bajaj-style daily attribution: buy commission books on the buy day,
// sell commission books on the sell day. When `range` is provided, only the
// portion of revenue whose attribution day falls in [start, end] is counted.
function calculateTradeRevenue(
  trade: Trade,
  client: Client | undefined,
  range?: { start: Date; end: Date },
): { revenue: number; turnover: number } {
  const buyVal = trade.purchaseLots * trade.lotSize * trade.purchasePrice;
  const sellVal = (trade.saleLots || 0) * trade.lotSize * (trade.salePrice || 0);
  const turnover = buyVal + sellVal;
  if (!client) return { revenue: 0, turnover };

  const c = dailyBrokerageContribution(trade, client.brokerageRates as any);
  let revenue = 0;
  if (range) {
    const startStr = istDay(range.start.toISOString());
    const endStr = istDay(range.end.toISOString());
    if (c.buyDay >= startStr && c.buyDay <= endStr) revenue += c.buy;
    if (c.sellDay && c.sellDay >= startStr && c.sellDay <= endStr) revenue += c.sell;
  } else {
    revenue = c.buy + c.sell;
  }
  return { revenue, turnover };
}

// ============================================================================
// SHARED STYLES
// ============================================================================

const headerCellSx = {
  fontWeight: 600,
  fontSize: '0.68rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  color: C.textSecondary,
  py: 1.5,
  borderBottom: `1px solid ${C.border}`,
  whiteSpace: 'nowrap' as const,
  bgcolor: '#F8FAFC',
};

const numCellSx = {
  fontWeight: 500,
  fontFeatureSettings: '"tnum"',
  fontVariantNumeric: 'tabular-nums' as const,
  fontSize: '0.85rem',
  color: C.textPrimary,
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DealerAnalytics() {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDealer, setExpandedDealer] = useState<string | null>(null);

  const [dateMode, setDateMode] = useState<DateMode>('day');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [tab, setTab] = useState<number>(0);
  const [focusDealer, setFocusDealer] = useState<string | null>(null); // chart-level filter

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [tradesData, clientsData, employeesData] = await Promise.all([
          tradeService.getTrades(),
          clientService.getClients(),
          salaryService.getEmployees(),
        ]);
        setTrades(tradesData);
        setClients(clientsData);
        setEmployees(employeesData.employees || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter trades by date range — include any trade whose buy day OR sell
  // day falls in the period (Bajaj-style attribution can split across days).
  const filteredTrades = useMemo(() => {
    const rng = getDateRange(dateMode, selectedDate);
    const base = trades.filter((t) => !t.isDeleted);
    if (!rng) return base;
    const startStr = istDay(rng.start.toISOString());
    const endStr = istDay(rng.end.toISOString());
    return base.filter((t) => {
      const buyDay = istDay(t.date || t.createdAt || '');
      const sellDay = (t.saleLots || 0) > 0 ? istDay(t.saleDate || t.date || t.createdAt || '') : null;
      return (buyDay >= startStr && buyDay <= endStr) ||
             (!!sellDay && sellDay >= startStr && sellDay <= endStr);
    });
  }, [trades, dateMode, selectedDate]);

  // Build dealer analytics data (scoped to filtered trades)
  const dealerData = useMemo((): DealerStats[] => {
    const cMap = new Map(clients.map((c) => [c.clientId, c]));
    const rng = getDateRange(dateMode, selectedDate) || undefined;
    const dMap = new Map<string, Map<string, ClientBreakdown>>();

    filteredTrades.forEach((t) => {
      const cl = cMap.get(t.clientId);
      const dealer = cl?.referredByName || 'Direct';
      if (!dMap.has(dealer)) dMap.set(dealer, new Map());
      const clientsMap = dMap.get(dealer)!;
      const cKey = t.clientId;

      if (!clientsMap.has(cKey)) {
        clientsMap.set(cKey, {
          clientId: cKey,
          clientName: t.clientName || cKey,
          revenue: 0,
          tradeCount: 0,
          turnover: 0,
          openTrades: 0,
          closedTrades: 0,
          tradeTypes: {},
        });
      }

      const entry = clientsMap.get(cKey)!;
      const { revenue, turnover } = calculateTradeRevenue(t, cl, rng);
      entry.revenue += revenue;
      entry.tradeCount += 1;
      entry.turnover += turnover;
      if (t.status === 'OPEN') entry.openTrades += 1;
      if (t.status === 'CLOSED') entry.closedTrades += 1;
      const tt = t.tradeType || 'Unknown';
      entry.tradeTypes[tt] = (entry.tradeTypes[tt] || 0) + 1;
    });

    return Array.from(dMap.entries())
      .map(([dealer, clientsMap]) => {
        const clientRows = Array.from(clientsMap.values()).sort((a, b) => b.revenue - a.revenue);
        const totalRevenue = clientRows.reduce((s, c) => s + c.revenue, 0);
        const totalTurnover = clientRows.reduce((s, c) => s + c.turnover, 0);
        const totalTrades = clientRows.reduce((s, c) => s + c.tradeCount, 0);
        const totalOpen = clientRows.reduce((s, c) => s + c.openTrades, 0);
        const totalClosed = clientRows.reduce((s, c) => s + c.closedTrades, 0);

        const tradeTypes: Record<string, number> = {};
        clientRows.forEach((c) => {
          Object.entries(c.tradeTypes).forEach(([k, v]) => {
            tradeTypes[k] = (tradeTypes[k] || 0) + v;
          });
        });

        return {
          dealer,
          clients: clientRows,
          totalRevenue,
          totalTurnover,
          totalTrades,
          totalOpen,
          totalClosed,
          clientCount: clientRows.length,
          avgRevenuePerTrade: totalTrades > 0 ? totalRevenue / totalTrades : 0,
          avgRevenuePerClient: clientRows.length > 0 ? totalRevenue / clientRows.length : 0,
          tradeTypes,
          revenueByType: {}, // filled below
        };
      })
      .sort((a, b) => b.totalRevenue - a.totalRevenue);
  }, [filteredTrades, clients, dateMode, selectedDate]);

  // Separately compute revenue by type (keeps dealerData memo simple)
  const dealerDataWithRevByType = useMemo(() => {
    const cMap = new Map(clients.map((c) => [c.clientId, c]));
    const rng = getDateRange(dateMode, selectedDate) || undefined;
    const map: Record<string, Record<string, number>> = {};
    filteredTrades.forEach((t) => {
      const cl = cMap.get(t.clientId);
      const dealer = cl?.referredByName || 'Direct';
      const { revenue } = calculateTradeRevenue(t, cl, rng);
      const tt = t.tradeType || 'Unknown';
      if (!map[dealer]) map[dealer] = {};
      map[dealer][tt] = (map[dealer][tt] || 0) + revenue;
    });
    return dealerData.map((d) => ({ ...d, revenueByType: map[d.dealer] || {} }));
  }, [dealerData, filteredTrades, clients, dateMode, selectedDate]);

  const grandTotalRevenue = dealerDataWithRevByType.reduce((s, d) => s + d.totalRevenue, 0);
  const grandTotalTurnover = dealerDataWithRevByType.reduce((s, d) => s + d.totalTurnover, 0);
  const grandTotalTrades = dealerDataWithRevByType.reduce((s, d) => s + d.totalTrades, 0);
  const grandTotalClients = dealerDataWithRevByType.reduce((s, d) => s + d.clientCount, 0);
  const topPerformer = dealerDataWithRevByType.length > 0 ? dealerDataWithRevByType[0] : null;
  const topThree = dealerDataWithRevByType.slice(0, 3);

  // Dealer colour assignment (stable by order)
  const dealerColor = (name: string) => {
    const idx = dealerDataWithRevByType.findIndex((d) => d.dealer === name);
    return CHART_PALETTE[idx >= 0 ? idx % CHART_PALETTE.length : 0];
  };

  // Time-series: revenue per day per dealer. Bajaj-style attribution can
  // place buy commission on one day and sell commission on another, so each
  // trade can contribute to up to two day buckets.
  const timeSeries = useMemo(() => {
    const cMap = new Map(clients.map((c) => [c.clientId, c]));
    const bucketMap = new Map<string, Record<string, number>>();
    filteredTrades.forEach((t) => {
      const cl = cMap.get(t.clientId);
      if (!cl) return;
      const dealer = cl.referredByName || 'Direct';
      const c = dailyBrokerageContribution(t, cl.brokerageRates as any);
      if (c.buy > 0 && c.buyDay) {
        if (!bucketMap.has(c.buyDay)) bucketMap.set(c.buyDay, {});
        const row = bucketMap.get(c.buyDay)!;
        row[dealer] = (row[dealer] || 0) + c.buy;
      }
      if (c.sell > 0 && c.sellDay) {
        if (!bucketMap.has(c.sellDay)) bucketMap.set(c.sellDay, {});
        const row = bucketMap.get(c.sellDay)!;
        row[dealer] = (row[dealer] || 0) + c.sell;
      }
    });
    return Array.from(bucketMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, row]) => ({ date, ...row }));
  }, [filteredTrades, clients]);

  // Revenue-share data for pie/donut
  const pieData = useMemo(
    () =>
      dealerDataWithRevByType.map((d) => ({
        name: d.dealer,
        value: d.totalRevenue,
        color: dealerColor(d.dealer),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealerDataWithRevByType]
  );

  // Trade-type composition per dealer (for stacked bar)
  const typeStackData = useMemo(
    () =>
      dealerDataWithRevByType.map((d) => {
        const row: Record<string, any> = { dealer: d.dealer };
        Object.entries(d.revenueByType).forEach(([k, v]) => {
          row[k] = v;
        });
        return row;
      }),
    [dealerDataWithRevByType]
  );
  const tradeTypeKeys = useMemo(() => {
    const keys = new Set<string>();
    dealerDataWithRevByType.forEach((d) => Object.keys(d.revenueByType).forEach((k) => keys.add(k)));
    return Array.from(keys);
  }, [dealerDataWithRevByType]);

  // ============================================================================
  // RENDER STATES
  // ============================================================================

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: C.pageBg, pb: 3 }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 1.5 }} />
          <Box flex={1}>
            <Skeleton variant="text" width={80} height={14} />
            <Skeleton variant="text" width={260} height={32} />
            <Skeleton variant="text" width={360} height={18} />
          </Box>
        </Box>
        <Grid container spacing={2.5} mb={3}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={6} md={2.4} key={i}>
              <Skeleton variant="rounded" height={110} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: C.pageBg, pb: 3 }}>
        <PageHeader eyebrow="Reports" title="Dealer Analytics" />
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const segmentedModes: { key: DateMode; label: string }[] = [
    { key: 'day', label: 'Day' },
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
    { key: 'quarter', label: 'Quarter' },
    { key: 'year', label: 'Year' },
    { key: 'all', label: 'All' },
  ];

  const pnlPositive = true; // unused placeholder for palette consistency

  // KPI cards
  const kpis: { label: string; value: string; sub?: string; icon: React.ReactNode; accent: string; accentBg: string }[] = [
    {
      label: 'Total Revenue',
      value: inr(grandTotalRevenue),
      sub: `${grandTotalTrades.toLocaleString('en-IN')} trades`,
      icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
      accent: C.positive,
      accentBg: '#D1FAE5',
    },
    {
      label: 'Total Turnover',
      value: inrCompact(grandTotalTurnover),
      sub: 'All dealers',
      icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />,
      accent: C.warning,
      accentBg: '#FEF3C7',
    },
    {
      label: 'Active Dealers',
      value: dealerDataWithRevByType.length.toString(),
      sub: `${employees.length} on roster`,
      icon: <GroupsOutlinedIcon sx={{ fontSize: 20 }} />,
      accent: C.primary,
      accentBg: '#EEF2FF',
    },
    {
      label: 'Clients Served',
      value: grandTotalClients.toString(),
      sub: 'In period',
      icon: <PeopleOutlineIcon sx={{ fontSize: 20 }} />,
      accent: C.blue,
      accentBg: '#DBEAFE',
    },
    {
      label: 'Top Performer',
      value: topPerformer ? topPerformer.dealer : '—',
      sub: topPerformer ? inr(topPerformer.totalRevenue) : '',
      icon: <EmojiEventsOutlinedIcon sx={{ fontSize: 20 }} />,
      accent: '#DB2777',
      accentBg: '#FCE7F3',
    },
  ];

  // Recharts custom tooltip (MIS-grade)
  const ChartTooltip = ({ active, payload, label, valueFormatter }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          border: `1px solid ${C.border}`,
          borderRadius: 1.5,
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
          p: 1.25,
          minWidth: 160,
        }}
      >
        {label && (
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: C.textPrimary, mb: 0.5, letterSpacing: '-0.01em' }}>
            {label}
          </Typography>
        )}
        {payload
          .slice()
          .sort((a: any, b: any) => (b.value || 0) - (a.value || 0))
          .map((p: any) => (
            <Box
              key={p.dataKey || p.name}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.25 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color || p.fill }} />
                <Typography sx={{ fontSize: '0.72rem', color: C.textSecondary, fontWeight: 500 }}>
                  {p.name || p.dataKey}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: C.textPrimary, fontFeatureSettings: '"tnum"' }}>
                {valueFormatter ? valueFormatter(p.value) : inr(p.value)}
              </Typography>
            </Box>
          ))}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.pageBg, pb: 3 }}>
      <PageHeader
        eyebrow="Reports"
        title="Dealer Analytics"
        subtitle="Agent performance, revenue, and client distribution"
        fallbackPath="/"
      />

      {/* ================= Date Range Toolbar ================= */}
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: 1.25,
          borderRadius: 2,
          bgcolor: C.surface,
          border: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        {/* Segmented mode switcher */}
        <Box sx={{ display: 'inline-flex', p: 0.375, bgcolor: '#F1F5F9', borderRadius: 1.5, gap: 0.25, flexWrap: 'wrap' }}>
          {segmentedModes.map((m) => {
            const active = dateMode === m.key;
            return (
              <Box
                key={m.key}
                onClick={() => {
                  setDateMode(m.key);
                  if (m.key !== 'day') setSelectedDate(new Date());
                }}
                sx={{
                  px: 1.5,
                  py: 0.625,
                  borderRadius: 1,
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: active ? C.textPrimary : C.textSecondary,
                  bgcolor: active ? C.surface : 'transparent',
                  boxShadow: active ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': { color: C.textPrimary },
                }}
              >
                {m.label}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ width: '1px', height: 24, bgcolor: C.border }} />
        {dateMode !== 'all' && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              size="small"
              onClick={() => setSelectedDate((d) => bumpDate(dateMode, d, -1))}
              sx={{
                width: 30,
                height: 30,
                border: `1px solid ${C.border}`,
                borderRadius: 1,
                color: C.textSecondary,
                '&:hover': { bgcolor: '#F8FAFC', color: C.textPrimary, borderColor: C.borderStrong },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 200,
                textAlign: 'center',
                bgcolor: '#F8FAFC',
                border: `1px solid ${C.border}`,
                borderRadius: 1,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: C.textPrimary,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {dateRangeLabel(dateMode, selectedDate)}
            </Box>
            <IconButton
              size="small"
              onClick={() => setSelectedDate((d) => bumpDate(dateMode, d, 1))}
              disabled={isNextDisabled(dateMode, selectedDate)}
              sx={{
                width: 30,
                height: 30,
                border: `1px solid ${C.border}`,
                borderRadius: 1,
                color: C.textSecondary,
                '&:hover': { bgcolor: '#F8FAFC', color: C.textPrimary, borderColor: C.borderStrong },
                '&.Mui-disabled': { opacity: 0.4 },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
            {(dateMode === 'day' && !isToday(selectedDate)) ||
            (dateMode !== 'day' &&
              !isSameDay(
                getDateRange(dateMode, selectedDate)?.end || new Date(),
                getDateRange(dateMode, new Date())?.end || new Date()
              )) ? (
              <Button
                size="small"
                onClick={() => setSelectedDate(new Date())}
                sx={{
                  ml: 0.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  color: C.primary,
                  px: 1.25,
                  minWidth: 'auto',
                  '&:hover': { bgcolor: '#EEF2FF' },
                }}
              >
                Today
              </Button>
            ) : null}
          </Box>
        )}
        {dateMode === 'all' && (
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: C.textSecondary }}>
            Showing all-time data
          </Typography>
        )}
      </Paper>

      {/* ================= KPI CARDS ================= */}
      <Grid container spacing={2.5} mb={3}>
        {kpis.map((k) => (
          <Grid item xs={6} md={2.4} key={k.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: C.surface,
                border: `1px solid ${C.border}`,
                transition: 'all 0.2s ease',
                height: '100%',
                '&:hover': {
                  borderColor: C.borderStrong,
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
                },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.25}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: C.textSecondary,
                    letterSpacing: '0.04em',
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                  }}
                >
                  {k.label}
                </Typography>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: 1.5,
                    bgcolor: k.accentBg,
                    color: k.accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {k.icon}
                </Box>
              </Box>
              <Typography
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: C.textPrimary,
                  letterSpacing: '-0.02em',
                  fontFeatureSettings: '"tnum"',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={k.value}
              >
                {k.value}
              </Typography>
              {k.sub && (
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    color: C.textSecondary,
                    mt: 0.25,
                    fontFeatureSettings: '"tnum"',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={k.sub}
                >
                  {k.sub}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ================= TOP 3 PODIUM ================= */}
      {topThree.length > 0 && (
        <Grid container spacing={2.5} mb={3}>
          {topThree.map((d, idx) => {
            const medals = ['#F59E0B', '#94A3B8', '#B45309']; // gold, silver, bronze
            const medalBg = ['#FEF3C7', '#F1F5F9', '#FED7AA'];
            const share = grandTotalRevenue > 0 ? (d.totalRevenue / grandTotalRevenue) * 100 : 0;
            return (
              <Grid item xs={12} md={4} key={d.dealer}>
                <Paper
                  elevation={0}
                  onClick={() => {
                    setFocusDealer(focusDealer === d.dealer ? null : d.dealer);
                    setExpandedDealer(d.dealer);
                  }}
                  sx={{
                    p: 2.25,
                    borderRadius: 2,
                    bgcolor: C.surface,
                    border: `1px solid ${focusDealer === d.dealer ? medals[idx] : C.border}`,
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: medals[idx],
                      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      transform: 'translate(30%, -30%)',
                      bgcolor: medalBg[idx],
                      opacity: 0.7,
                    }}
                  />
                  <Box sx={{ position: 'relative' }}>
                    <Box display="flex" alignItems="center" gap={1.25} mb={1.25}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          bgcolor: medalBg[idx],
                          color: medals[idx],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <WorkspacePremiumIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            color: medals[idx],
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            lineHeight: 1,
                          }}
                        >
                          Rank #{idx + 1}
                        </Typography>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: C.textPrimary, lineHeight: 1.3 }}>
                          {d.dealer}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '1.4rem',
                        fontWeight: 700,
                        color: C.positive,
                        letterSpacing: '-0.02em',
                        fontFeatureSettings: '"tnum"',
                        lineHeight: 1.1,
                      }}
                    >
                      {inr(d.totalRevenue)}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5} mt={1}>
                      <Chip
                        label={`${share.toFixed(1)}% share`}
                        size="small"
                        sx={{
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          height: 22,
                          bgcolor: medalBg[idx],
                          color: medals[idx],
                        }}
                      />
                      <Typography sx={{ fontSize: '0.72rem', color: C.textSecondary, fontFeatureSettings: '"tnum"' }}>
                        {d.clientCount} clients · {d.totalTrades} trades
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ================= TABS ================= */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${C.border}`,
          bgcolor: C.surface,
          overflow: 'hidden',
          mb: 3,
        }}
      >
        <Box sx={{ px: 2, pt: 1, borderBottom: `1px solid ${C.border}` }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 42,
              '& .MuiTab-root': {
                minHeight: 42,
                py: 1,
                fontSize: '0.83rem',
                fontWeight: 600,
                color: C.textSecondary,
                textTransform: 'none',
                letterSpacing: '-0.01em',
                '&.Mui-selected': { color: C.textPrimary },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: C.primary,
                height: 2.5,
                borderRadius: 1.25,
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Revenue Share" />
            <Tab label="Trends" />
            <Tab label="Composition" />
            <Tab label="Leaderboard" />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          {/* ============= OVERVIEW ============= */}
          {tab === 0 && (
            <Grid container spacing={2.5}>
              {/* Revenue distribution horizontal bars */}
              <Grid item xs={12} md={7}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#FAFBFC', border: `1px solid ${C.border}` }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: C.textPrimary, mb: 2 }}>
                    Revenue Distribution
                  </Typography>
                  {dealerDataWithRevByType.length === 0 ? (
                    <Typography sx={{ py: 4, textAlign: 'center', fontSize: '0.85rem', color: C.textSecondary }}>
                      No dealer data in selected period.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {dealerDataWithRevByType.map((d) => {
                        const pct = grandTotalRevenue > 0 ? (d.totalRevenue / grandTotalRevenue) * 100 : 0;
                        const maxRev = dealerDataWithRevByType[0]?.totalRevenue || 1;
                        const widthPct = (d.totalRevenue / maxRev) * 100;
                        const color = dealerColor(d.dealer);
                        return (
                          <Box
                            key={d.dealer}
                            onClick={() => setFocusDealer(focusDealer === d.dealer ? null : d.dealer)}
                            sx={{ cursor: 'pointer', opacity: focusDealer && focusDealer !== d.dealer ? 0.45 : 1, transition: 'opacity 0.15s ease' }}
                          >
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                                <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: C.textPrimary }}>
                                  {d.dealer}
                                </Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: C.textSecondary, fontWeight: 500 }}>
                                  · {d.clientCount} clients · {d.totalTrades} trades
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: C.positive, fontFeatureSettings: '"tnum"' }}>
                                  {inr(d.totalRevenue)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: C.textSecondary, minWidth: 44, textAlign: 'right', fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                                  {pct.toFixed(1)}%
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ height: 10, bgcolor: '#F1F5F9', borderRadius: 999, overflow: 'hidden' }}>
                              <Box
                                sx={{
                                  width: `${Math.max(widthPct, 1)}%`,
                                  height: '100%',
                                  background: `linear-gradient(90deg, ${color}, ${color}CC)`,
                                  borderRadius: 999,
                                  transition: 'width 0.6s ease-in-out',
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Donut share */}
              <Grid item xs={12} md={5}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#FAFBFC', border: `1px solid ${C.border}`, height: '100%' }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: C.textPrimary, mb: 1.5 }}>
                    Revenue Share
                  </Typography>
                  {pieData.length === 0 ? (
                    <Typography sx={{ py: 4, textAlign: 'center', fontSize: '0.85rem', color: C.textSecondary }}>
                      No data.
                    </Typography>
                  ) : (
                    <Box sx={{ position: 'relative', width: '100%', height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={95}
                            paddingAngle={2}
                            stroke="none"
                            onClick={(e: any) => setFocusDealer(focusDealer === e.name ? null : e.name)}
                          >
                            {pieData.map((entry) => (
                              <Cell
                                key={entry.name}
                                fill={entry.color}
                                fillOpacity={focusDealer && focusDealer !== entry.name ? 0.35 : 1}
                                style={{ cursor: 'pointer', outline: 'none' }}
                              />
                            ))}
                          </Pie>
                          <RTooltip content={<ChartTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center',
                          pointerEvents: 'none',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: C.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                          Total
                        </Typography>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: C.textPrimary, fontFeatureSettings: '"tnum"' }}>
                          {inrCompact(grandTotalRevenue)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* ============= REVENUE SHARE ============= */}
          {tab === 1 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#FAFBFC', border: `1px solid ${C.border}`, height: 380 }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: C.textPrimary, mb: 1 }}>
                    Revenue Share by Dealer
                  </Typography>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="92%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={130}
                          label={(entry: any) => `${entry.name}: ${((entry.percent || 0) * 100).toFixed(1)}%`}
                          labelLine={false}
                          stroke="#FFFFFF"
                          strokeWidth={2}
                        >
                          {pieData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <RTooltip content={<ChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 6, fontSize: '0.85rem', color: C.textSecondary }}>No data</Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#FAFBFC', border: `1px solid ${C.border}`, height: 380 }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: C.textPrimary, mb: 1 }}>
                    Clients per Dealer
                  </Typography>
                  {dealerDataWithRevByType.length > 0 ? (
                    <ResponsiveContainer width="100%" height="92%">
                      <BarChart data={dealerDataWithRevByType} layout="vertical" margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: C.textSecondary }} />
                        <YAxis dataKey="dealer" type="category" width={110} tick={{ fontSize: 11, fill: C.textPrimary, fontWeight: 500 }} />
                        <RTooltip content={<ChartTooltip valueFormatter={(v: number) => `${v} clients`} />} />
                        <Bar dataKey="clientCount" radius={[0, 6, 6, 0]}>
                          {dealerDataWithRevByType.map((d) => (
                            <Cell key={d.dealer} fill={dealerColor(d.dealer)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography sx={{ textAlign: 'center', py: 6, fontSize: '0.85rem', color: C.textSecondary }}>No data</Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* ============= TRENDS ============= */}
          {tab === 2 && (
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#FAFBFC', border: `1px solid ${C.border}`, height: 420 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: C.textPrimary }}>
                  Revenue Over Time {focusDealer ? `· ${focusDealer}` : '· All Dealers'}
                </Typography>
                {focusDealer && (
                  <Button
                    size="small"
                    onClick={() => setFocusDealer(null)}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: C.primary, px: 1, '&:hover': { bgcolor: '#EEF2FF' } }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
              {timeSeries.length === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 8, fontSize: '0.85rem', color: C.textSecondary }}>
                  No trades in the selected period.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="88%">
                  <AreaChart data={timeSeries} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                    <defs>
                      {dealerDataWithRevByType.map((d) => (
                        <linearGradient key={d.dealer} id={`grad-${d.dealer.replace(/\s+/g, '-')}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={dealerColor(d.dealer)} stopOpacity={0.6} />
                          <stop offset="95%" stopColor={dealerColor(d.dealer)} stopOpacity={0.05} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: C.textSecondary }}
                      tickFormatter={(d: string) => {
                        const date = new Date(d);
                        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                      }}
                    />
                    <YAxis tick={{ fontSize: 11, fill: C.textSecondary }} tickFormatter={(v: number) => inrCompact(v)} width={60} />
                    <RTooltip
                      content={
                        <ChartTooltip
                          valueFormatter={(v: number) => inr(v)}
                        />
                      }
                    />
                    <Legend
                      wrapperStyle={{ fontSize: '0.72rem', paddingTop: 8 }}
                      iconType="circle"
                      onClick={(e: any) => setFocusDealer(focusDealer === e.value ? null : e.value)}
                    />
                    {dealerDataWithRevByType.map((d) => (
                      <Area
                        key={d.dealer}
                        type="monotone"
                        dataKey={d.dealer}
                        stackId="rev"
                        stroke={dealerColor(d.dealer)}
                        fill={`url(#grad-${d.dealer.replace(/\s+/g, '-')})`}
                        strokeWidth={2}
                        fillOpacity={focusDealer && focusDealer !== d.dealer ? 0.15 : 1}
                        hide={false}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Paper>
          )}

          {/* ============= COMPOSITION ============= */}
          {tab === 3 && (
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#FAFBFC', border: `1px solid ${C.border}`, height: 420 }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: C.textPrimary, mb: 1 }}>
                Revenue Composition by Trade Type
              </Typography>
              {typeStackData.length === 0 || tradeTypeKeys.length === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 8, fontSize: '0.85rem', color: C.textSecondary }}>
                  No trade-type revenue in the selected period.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="88%">
                  <BarChart data={typeStackData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="dealer" tick={{ fontSize: 11, fill: C.textPrimary, fontWeight: 500 }} />
                    <YAxis tick={{ fontSize: 11, fill: C.textSecondary }} tickFormatter={(v: number) => inrCompact(v)} width={60} />
                    <RTooltip content={<ChartTooltip valueFormatter={(v: number) => inr(v)} />} />
                    <Legend wrapperStyle={{ fontSize: '0.72rem', paddingTop: 8 }} iconType="circle" />
                    {tradeTypeKeys.map((key, idx) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        stackId="type"
                        fill={TYPE_COLOR[key] || CHART_PALETTE[idx % CHART_PALETTE.length]}
                        radius={idx === tradeTypeKeys.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          )}

          {/* ============= LEADERBOARD ============= */}
          {tab === 4 && (
            <Paper elevation={0} sx={{ borderRadius: 2, bgcolor: C.surface, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['#', 'Dealer', 'Clients', 'Trades', 'Open', 'Closed', 'Turnover', 'Revenue', 'Avg / Trade', 'Avg / Client', 'Share'].map((h, i) => (
                        <TableCell
                          key={h}
                          align={i <= 1 ? 'left' : 'right'}
                          sx={headerCellSx}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dealerDataWithRevByType.map((d, idx) => {
                      const share = grandTotalRevenue > 0 ? (d.totalRevenue / grandTotalRevenue) * 100 : 0;
                      const color = dealerColor(d.dealer);
                      return (
                        <TableRow
                          key={d.dealer}
                          hover
                          onClick={() => setFocusDealer(focusDealer === d.dealer ? null : d.dealer)}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: focusDealer === d.dealer ? '#F8FAFC' : C.surface,
                            '&:hover': { bgcolor: '#F8FAFC' },
                            '& td': { borderBottom: `1px solid ${C.border}`, py: 1.5 },
                          }}
                        >
                          <TableCell>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: C.textMuted, fontFeatureSettings: '"tnum"' }}>
                              {idx + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                              <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: C.textPrimary }}>
                                {d.dealer}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={numCellSx}>{d.clientCount}</TableCell>
                          <TableCell align="right" sx={numCellSx}>{d.totalTrades}</TableCell>
                          <TableCell align="right" sx={{ ...numCellSx, color: C.blue }}>{d.totalOpen}</TableCell>
                          <TableCell align="right" sx={{ ...numCellSx, color: C.positive }}>{d.totalClosed}</TableCell>
                          <TableCell align="right" sx={numCellSx}>{inr(d.totalTurnover)}</TableCell>
                          <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700, color: C.positive }}>{inr(d.totalRevenue)}</TableCell>
                          <TableCell align="right" sx={{ ...numCellSx, color: C.textSecondary }}>{inrPrecise(d.avgRevenuePerTrade)}</TableCell>
                          <TableCell align="right" sx={{ ...numCellSx, color: C.textSecondary }}>{inr(d.avgRevenuePerClient)}</TableCell>
                          <TableCell align="right">
                            <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                              <Box sx={{ width: 60, height: 6, bgcolor: '#F1F5F9', borderRadius: 3, overflow: 'hidden' }}>
                                <Box sx={{ width: `${share}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
                              </Box>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: C.textPrimary, minWidth: 42, textAlign: 'right', fontFeatureSettings: '"tnum"' }}>
                                {share.toFixed(1)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Total row */}
                    <TableRow sx={{ bgcolor: '#F8FAFC', '& td': { fontWeight: 700, borderTop: `1px solid ${C.borderStrong}`, py: 1.75 } }}>
                      <TableCell />
                      <TableCell sx={{ color: C.textPrimary, fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                        Total · {dealerDataWithRevByType.length} dealers
                      </TableCell>
                      <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700 }}>{grandTotalClients}</TableCell>
                      <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700 }}>{grandTotalTrades}</TableCell>
                      <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700, color: C.blue }}>
                        {dealerDataWithRevByType.reduce((s, d) => s + d.totalOpen, 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700, color: C.positive }}>
                        {dealerDataWithRevByType.reduce((s, d) => s + d.totalClosed, 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700 }}>{inr(grandTotalTurnover)}</TableCell>
                      <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700, color: C.positive }}>{inr(grandTotalRevenue)}</TableCell>
                      <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700, color: C.textSecondary }}>
                        {inrPrecise(grandTotalTrades > 0 ? grandTotalRevenue / grandTotalTrades : 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700, color: C.textSecondary }}>
                        {inr(dealerDataWithRevByType.length > 0 ? grandTotalRevenue / dealerDataWithRevByType.length : 0)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Paper>

      {/* ================= DEEP-DIVE DEALER CARDS ================= */}
      <Box>
        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: C.textPrimary, mb: 1.5 }}>
          Dealer Details
        </Typography>
        {dealerDataWithRevByType.length === 0 && (
          <Paper
            elevation={0}
            sx={{ p: 4, borderRadius: 2, bgcolor: C.surface, border: `1px solid ${C.border}`, textAlign: 'center' }}
          >
            <Typography sx={{ fontSize: '0.9rem', color: C.textSecondary }}>
              No dealer activity in the selected period.
            </Typography>
          </Paper>
        )}
        {dealerDataWithRevByType.map((d) => {
          const isExpanded = expandedDealer === d.dealer;
          const color = dealerColor(d.dealer);
          const share = grandTotalRevenue > 0 ? (d.totalRevenue / grandTotalRevenue) * 100 : 0;
          return (
            <Paper
              key={d.dealer}
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${isExpanded ? color : C.border}`,
                mb: 1.5,
                overflow: 'hidden',
                transition: 'border-color 0.2s ease',
                bgcolor: C.surface,
              }}
            >
              {/* Header */}
              <Box
                onClick={() => setExpandedDealer(isExpanded ? null : d.dealer)}
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  bgcolor: isExpanded ? '#F8FAFC' : C.surface,
                  '&:hover': { bgcolor: '#F8FAFC' },
                  transition: 'background-color 0.15s ease',
                  gap: 2,
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                  <Box minWidth={0}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: C.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {d.dealer}
                    </Typography>
                    <Box display="flex" gap={1.5} mt={0.25} flexWrap="wrap">
                      <Typography sx={{ fontSize: '0.72rem', color: C.textSecondary, fontFeatureSettings: '"tnum"' }}>
                        {d.clientCount} clients
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: C.textSecondary, fontFeatureSettings: '"tnum"' }}>
                        · {d.totalTrades} trades
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: C.textSecondary, fontFeatureSettings: '"tnum"' }}>
                        · {share.toFixed(1)}% share
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box textAlign="right">
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: C.positive, fontFeatureSettings: '"tnum"', lineHeight: 1.1 }}>
                      {inr(d.totalRevenue)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: C.textSecondary, fontFeatureSettings: '"tnum"', mt: 0.25 }}>
                      Turnover {inrCompact(d.totalTurnover)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    sx={{ border: `1px solid ${C.border}`, borderRadius: 1, color: C.textSecondary, '&:hover': { bgcolor: '#F8FAFC' } }}
                  >
                    {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Box>
              </Box>

              {/* Expanded */}
              <Collapse in={isExpanded}>
                <Box sx={{ borderTop: `1px solid ${C.border}`, bgcolor: '#FAFBFC' }}>
                  {/* Trade-type breakdown */}
                  <Box sx={{ p: 2, borderBottom: `1px solid ${C.border}` }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                      Trade Type Breakdown
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {Object.entries(d.tradeTypes)
                        .sort((a, b) => b[1] - a[1])
                        .map(([type, count]) => {
                          const rev = d.revenueByType[type] || 0;
                          const c = TYPE_COLOR[type] || C.textSecondary;
                          return (
                            <Box
                              key={type}
                              sx={{
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 1,
                                border: `1px solid ${c}40`,
                                bgcolor: `${c}0D`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                              }}
                            >
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: c }}>{type}</Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: C.textSecondary, fontWeight: 600 }}>
                                {count}
                              </Typography>
                              <Box sx={{ width: '1px', height: 10, bgcolor: `${c}60` }} />
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: c, fontFeatureSettings: '"tnum"' }}>
                                {inrCompact(rev)}
                              </Typography>
                            </Box>
                          );
                        })}
                    </Box>
                  </Box>

                  {/* Client breakdown table */}
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                      Client Breakdown
                    </Typography>
                    <Paper elevation={0} sx={{ borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${C.border}`, bgcolor: C.surface }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {['Client', 'Trades', 'Open', 'Closed', 'Turnover', 'Revenue', 'Mix'].map((h, i) => (
                              <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={headerCellSx}>
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {d.clients.map((c) => {
                            const eCount = (c.tradeTypes[TradeType.EQUITY] || 0) + (c.tradeTypes[TradeType.STOCK] || 0) + (c.tradeTypes[TradeType.INTRA_STOCK] || 0);
                            const fCount = c.tradeTypes[TradeType.FUTURE] || 0;
                            const oCount = c.tradeTypes[TradeType.OPTION] || 0;
                            return (
                              <TableRow key={c.clientId} hover sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                                <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, color: C.textPrimary }}>
                                  {c.clientName}
                                </TableCell>
                                <TableCell align="right" sx={numCellSx}>{c.tradeCount}</TableCell>
                                <TableCell align="right" sx={{ ...numCellSx, color: C.blue }}>{c.openTrades}</TableCell>
                                <TableCell align="right" sx={{ ...numCellSx, color: C.positive }}>{c.closedTrades}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{inr(c.turnover)}</TableCell>
                                <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700, color: C.positive }}>
                                  {inr(c.revenue)}
                                </TableCell>
                                <TableCell align="right">
                                  <Box display="flex" gap={0.5} justifyContent="flex-end">
                                    {eCount > 0 && (
                                      <Chip label={`E ${eCount}`} size="small" sx={{ fontSize: '0.62rem', height: 20, fontWeight: 700, bgcolor: `${C.blue}14`, color: C.blue }} />
                                    )}
                                    {fCount > 0 && (
                                      <Chip label={`F ${fCount}`} size="small" sx={{ fontSize: '0.62rem', height: 20, fontWeight: 700, bgcolor: `${C.primary}14`, color: C.primary }} />
                                    )}
                                    {oCount > 0 && (
                                      <Chip label={`O ${oCount}`} size="small" sx={{ fontSize: '0.62rem', height: 20, fontWeight: 700, bgcolor: `${C.warning}14`, color: C.warning }} />
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {/* Dealer subtotal */}
                          <TableRow sx={{ bgcolor: '#F8FAFC', '& td': { fontWeight: 700, borderTop: `1px solid ${C.borderStrong}` } }}>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: C.textPrimary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Total
                            </TableCell>
                            <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700 }}>{d.totalTrades}</TableCell>
                            <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700, color: C.blue }}>{d.totalOpen}</TableCell>
                            <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700, color: C.positive }}>{d.totalClosed}</TableCell>
                            <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700 }}>{inr(d.totalTurnover)}</TableCell>
                            <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700, color: C.positive }}>{inr(d.totalRevenue)}</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Paper>
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Box>
      {/* dummy ref to silence unused-var warnings on rarely-used helpers */}
      {pnlPositive ? null : null}
    </Box>
  );
}
